import { useCallback, useEffect, useState } from 'react';

const MARKET_URL = 'https://open.er-api.com/v6/latest/USD';
const STORE = 'gourdex.rate';
const OTHERS = ['eur', 'cad', 'dop'];

const round2 = (n) => Math.round(n * 100) / 100;

function remembered() {
  try {
    const saved = JSON.parse(localStorage.getItem(STORE));
    return saved?.rate > 0 ? saved : null;
  } catch {
    return null;
  }
}

/** { eur, cad, dop }: how many of each a US dollar is, from a market answer — or nothing. */
function perDollar(rates) {
  const per = {};
  for (const c of OTHERS) {
    const n = Number(rates?.[c.toUpperCase()]);
    per[c] = n > 0 && n < 1000 ? n : null;
  }
  return per;
}

/**
 * Fresh figures: { rate, origin, per, at }. `rate` is goud per US dollar;
 * `origin` is who published it — 'brh' (the Banque de la République d'Haïti's
 * taux de référence) or 'er-api' (the market rate from open.er-api.com) — and
 * the screen names it. `per` is what a dollar is in the other currencies, on
 * the market, so the gourde can be given against those too.
 */
async function freshRate() {
  // Our own server first: it reads the BRH's page, which a browser cannot (no CORS).
  try {
    const res = await fetch('/api/rate');
    const data = res.ok ? await res.json() : null;
    if (data?.rate > 0) return { rate: round2(data.rate), origin: data.source === 'brh' ? 'brh' : 'er-api', per: data.per || null, at: Date.now() };
  } catch { /* no server behind this page, or it is down: ask the market directly */ }
  const res = await fetch(MARKET_URL);
  if (!res.ok) throw new Error(`rate_${res.status}`);
  const rates = (await res.json())?.rates;
  const rate = round2(Number(rates?.HTG));
  if (!(rate > 0)) throw new Error('rate_missing');
  return { rate, origin: 'er-api', per: perDollar(rates), at: Date.now() };
}

/**
 * Goud per unit of `foreign` — the US dollar, the euro, the Canadian dollar or
 * the Dominican peso.
 *
 * `source` says where `rate` comes from:
 *   'loading'      asking, nothing to show yet
 *   'live'         fetched just now
 *   'cached'       the last rate we were given, while a fresh one is fetched or after that failed
 *   'manual'       typed by the user (this session only, for this currency — a
 *                  stale typed rate quietly reused next week would be worse than asking again)
 *   'unavailable'  no rate at all: `rate` is null and the user must type one
 *
 * `origin` is who the figure is from: 'brh', 'er-api', or 'brh+er-api' for a
 * currency other than the dollar — the BRH's dollar crossed with the market's
 * rate between that currency and the dollar.
 *
 * There is deliberately no default number. A converter that falls back to a
 * made-up rate gives confident wrong answers.
 */
export function useExchangeRate(foreign = 'usd') {
  const [live, setLive] = useState(remembered);          // { rate, origin, per, at } | null
  const [fetched, setFetched] = useState('loading');     // 'loading' | 'ok' | 'failed'
  const [manual, setManual] = useState(null);            // { currency, rate } | null

  useEffect(() => {
    let mounted = true;
    freshRate()
      .then((fresh) => {
        try { localStorage.setItem(STORE, JSON.stringify(fresh)); } catch {}
        if (mounted) { setLive(fresh); setFetched('ok'); }
      })
      .catch((e) => {
        console.error('Exchange rate fetch failed:', e);
        if (mounted) setFetched('failed');
      });
    return () => { mounted = false; };
  }, []);

  const setManualRate = useCallback((rate) => setManual(rate > 0 ? { currency: foreign, rate } : null), [foreign]);
  const clearManualRate = useCallback(() => setManual(null), []);

  // The live figure for this currency: the dollar's as given, the others crossed through it.
  const crossed = foreign === 'usd' ? live?.rate : live?.per?.[foreign] ? round2(live.rate / live.per[foreign]) : null;
  const typed = manual?.currency === foreign ? manual.rate : null;
  const liveOrigin = live?.origin || 'er-api';   // a rate remembered from before origins were kept was the market's

  const source = typed != null ? 'manual'
    : crossed == null ? (fetched === 'loading' ? 'loading' : 'unavailable')
    : fetched === 'ok' ? 'live'
    : 'cached';

  return {
    rate: typed ?? crossed ?? null,
    source,
    origin: typed == null && crossed != null ? (foreign === 'usd' || liveOrigin === 'er-api' ? liveOrigin : 'brh+er-api') : null,
    updatedAt: typed == null ? live?.at ?? null : null,
    setManualRate,
    clearManualRate,
  };
}
